import { useEffect, useState } from 'react';
import { THEMES, getThemeById, type ThemePreset } from '@/styles/themes/presets';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import { trackEvent } from '@/lib/analytics';

/**
 * useTheme — reads theme from user_settings.theme_json, applies CSS variables to <html>.
 * Falls back to Obsidian Night if no theme is set.
 */
export function useTheme() {
  const settings = useAuthStore((s) => s.settings);
  const user = useAuthStore((s) => s.user);
  const [activeTheme, setActiveTheme] = useState<ThemePreset>(THEMES[0]!);

  // Apply from DB settings when available.
  useEffect(() => {
    if (!settings) return;
    const themeId = settings.theme_json?.themeId ?? window.localStorage.getItem('johnicorn:theme') ?? 'obsidian';
    const theme = getThemeById(themeId);
    setActiveTheme(theme);
    applyTheme(theme);
  }, [settings]);

  // Early fallback before auth/settings hydrate.
  useEffect(() => {
    if (settings) return;
    const fallbackId = window.localStorage.getItem('johnicorn:theme') ?? 'obsidian';
    const fallbackTheme = getThemeById(fallbackId);
    setActiveTheme(fallbackTheme);
    applyTheme(fallbackTheme);
  }, [settings]);

  /**
   * Switch to a different theme preset.
   * Updates CSS vars immediately and persists to DB.
   */
  const switchTheme = async (themeId: string) => {
    const theme = getThemeById(themeId);
    setActiveTheme(theme);
    applyTheme(theme);
    window.localStorage.setItem('johnicorn:theme', theme.id);
    trackEvent('theme_change', { theme: theme.id });

    // Persist to user_settings if logged in
    if (user) {
      try {
        const prev = useAuthStore.getState().settings?.theme_json ?? {};
        await supabase
          .from('user_settings')
          .update({
            theme_json: { ...prev, themeId: theme.id },
          })
          .eq('user_id', user.id);
      } catch (err) {
        console.error('Failed to save theme:', err);
      }
    }
  };

  return {
    activeTheme,
    themes: THEMES,
    switchTheme,
  };
}

/**
 * Apply a theme's CSS variables to the <html> element.
 */
function applyTheme(theme: ThemePreset) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme.id);
}
